/**
 * ForecastRollupReport — /#/reports/forecast-rollup
 *
 * Bucketed by RULE-FORECAST-1: Commit, Best Case, Pipeline,
 * Pipeline+flag (timing = 50), Push/Loss-only (timing below 50).
 * Each row: deal count, total weighted value, total displayed value.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, RED, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import { ForecastBucketRow, downloadCsv, listForecastRollup, toCsv } from "@/lib/reports-saturday";
import { fmtUSD } from "@/lib/reporting-live";

// W64 canon bucket colors.
function bucketColor(b: ForecastBucketRow["bucket"]): string {
  switch (b) {
    case "Commit":           return "#16A34A";
    case "Upside":           return "#16A34A";
    case "Best Case":        return "#EAB308";
    case "Pipeline":         return "#EA580C";
    case "Pipeline+flag":    return "#EA580C";
    case "Omit":
    case "Push/Loss-only":
    case "Push":             return "#DC2626";
    case "Loss":             return "#8E8E94";
    default:                 return "#8E8E94";
  }
}

export default function ForecastRollupReport() {
  const [rows, setRows] = useState<ForecastBucketRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listForecastRollup();
        if (!cancelled) setRows(r);
      } catch (err: any) {
        console.error("[ForecastRollupReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => {
    if (!rows) return { deals: 0, weighted: 0, displayed: 0 };
    return rows.reduce(
      (acc, r) => ({
        deals: acc.deals + r.deal_count,
        weighted: acc.weighted + r.total_weighted_value,
        displayed: acc.displayed + r.total_displayed_value,
      }),
      { deals: 0, weighted: 0, displayed: 0 },
    );
  }, [rows]);

  const csv = useMemo(() => {
    if (!rows) return "";
    return toCsv(rows.map((r) => ({
      bucket: r.bucket,
      deal_count: r.deal_count,
      total_weighted_value: r.total_weighted_value,
      total_displayed_value: r.total_displayed_value,
    })));
  }, [rows]);

  return (
    <ReportShell
      title="Forecast Roll-Up"
      subtitle="RULE-FORECAST-1 buckets, with weighted and displayed totals."
      rightLabel="Forecast Roll-Up"
      onExport={rows && rows.length > 0 ? () => downloadCsv("forecast-rollup.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !rows ? (
        <LoadingState />
      ) : totals.deals === 0 ? (
        <EmptyState message="No timing records yet. Data activates as reps complete CP10 timing checkpoints." />
      ) : (
        <div style={{ overflowX: "auto", border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr>
                <th style={thStyle}>RULE-FORECAST-1 Bucket</th>
                <th style={thStyle}>Deals</th>
                <th style={thStyle}>Total weighted value</th>
                <th style={thStyle}>Total displayed value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.bucket}>
                  <td style={tdStyle}>
                    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, background: `${bucketColor(r.bucket)}22`, color: bucketColor(r.bucket), fontSize: 12, fontWeight: 700 }}>
                      {r.bucket}
                    </span>
                  </td>
                  <td style={tdStyle}>{r.deal_count}</td>
                  <td style={tdStyle}>{fmtUSD(r.total_weighted_value)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{fmtUSD(r.total_displayed_value)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...tdStyle, fontWeight: 800 }}>Total</td>
                <td style={{ ...tdStyle, fontWeight: 800 }}>{totals.deals}</td>
                <td style={{ ...tdStyle, fontWeight: 800 }}>{fmtUSD(totals.weighted)}</td>
                <td style={{ ...tdStyle, fontWeight: 800 }}>{fmtUSD(totals.displayed)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
}
